import React from "react";
import { Helmet } from "react-helmet";
import config from "appConfig";
import { asset } from "../services";

const defaultDescription =
  "Index Oglasi - Najbrže rastući oglasnik u Hrvatskoj. Predajte oglas za svoj auto, stan, posao ili apartman POTPUNO BESPLATNO. Sad i zauvijek, jer besplatni oglasi trebaju biti - besplatni.";

// Default OG image used only as a last resort — pages that render real
// content should always set `ogImage` explicitly so Discover and social
// previews pick up the actual listing image, not this fallback.
// og-image.jpg is a 1200x630 (FB-recommended size) brand composite —
// Index Oglasi logo centred on a white background. Its dimensions are
// emitted alongside so FB stops warning about missing image dimensions
// on ads with no uploaded photo.
const defaultOgImage = asset.getImageUrl("og-image.jpg");
const defaultOgImageWidth = 1200;
const defaultOgImageHeight = 630;

interface Props {
  title?: string;
  desc?: string;
  index?: boolean;
  ogUrl?: string;
  ogTitle?: string;
  ogDesc?: string;
  canonical?: string;
  ogImage?: string;
  // Explicit dimensions for `ogImage`. Set these ONLY when you know the
  // image URL always returns that exact size (e.g. `?m=fb` → 1200x630).
  // LinkedIn/WhatsApp refuse to render previews without dimensions; FB
  // and Googlebot verify and discard the tag if the real image differs.
  ogImageWidth?: number;
  ogImageHeight?: number;
  // "website" for landing/listing pages, "article" for editorial content
  // (MakeModelInfo, real-estate price articles), "product" only on actual
  // product/offer pages (ad details). Previously hard-coded to "product"
  // everywhere, which mislabelled every landing page to Google and FB.
  ogType?: "website" | "article" | "product";
  // Pass "summary_large_image" on pages with a real content image (ad
  // details, info pages) so Twitter/X renders the big preview card.
  twitterCard?: "summary" | "summary_large_image";
  // JSON-LD payload (Product, Vehicle, Article, BreadcrumbList, …). Pass
  // one object or an array; each is serialised into its own script tag.
  // Discover requires structured data for the rich card eligibility.
  structuredData?: object | object[];
}

export default function HelmetComponent(props: Props) {
  const {
    title,
    desc = defaultDescription,
    index = true,
    ogUrl = window.location.href,
    ogTitle,
    ogDesc,
    canonical = window.location.href.split("?")[0].split("#")[0],
    ogImage = defaultOgImage,
    // Width/height default to the known dimensions of defaultOgImage only
    // when caller hasn't passed their own ogImage — if the caller provides
    // ogImage without dimensions, we have no way to guess them and emit
    // nothing (FB/LinkedIn will measure the fetched image themselves).
    ogImageWidth = props.ogImage ? undefined : defaultOgImageWidth,
    ogImageHeight = props.ogImage ? undefined : defaultOgImageHeight,
    ogType = "website",
    twitterCard = "summary_large_image",
    structuredData
  } = props;

  const jsonLdItems = structuredData
    ? (Array.isArray(structuredData) ? structuredData : [structuredData])
    : [];

  const isHttps = ogImage && ogImage.startsWith("https://");

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <meta charSet="utf-8" />
      {title && <title>{title}</title>}
      {desc && <meta name="description" id="ad-description" content={desc} />}
      {!index && <meta name="robots" content="noindex" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {(title || ogTitle) && <meta property="og:title" content={ogTitle || title} />}
      {(desc || ogDesc) && <meta property="og:description" content={ogDesc || desc} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && isHttps && <meta property="og:image:secure_url" content={ogImage} />}
      {ogImage && <meta property="og:image:type" content="image/jpeg" />}
      {ogImageWidth && <meta property="og:image:width" content={String(ogImageWidth)} />}
      {ogImageHeight && <meta property="og:image:height" content={String(ogImageHeight)} />}
      {/* Instagram DM link previews reportedly use og:image:alt when picking
          whether to render the image card at all — without it the preview
          sometimes falls back to the broken-placeholder state. Fall back to
          the title so the tag is always present when ogImage is. */}
      {ogImage && (ogTitle || title) && <meta property="og:image:alt" content={ogTitle || title} />}
      {/* fb:app_id silences the "Missing Properties" warning in FB Sharing
          Debugger and is required for Insights / message-share flows. */}
      {config.facebookAppId && <meta property="fb:app_id" content={config.facebookAppId} />}

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {ogUrl && <meta property="twitter:url" content={ogUrl} />}
      <meta property="twitter:title" content={ogTitle || title} />
      <meta property="twitter:description" content={ogDesc || desc} />
      {ogImage && <meta property="twitter:image" content={ogImage} />}

      {/* Structured data (Discover rich card eligibility) */}
      {jsonLdItems.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}



// WEBPACK FOOTER //
// ./src/utils/HelmetHelper.tsx